import { useEffect, useState } from 'react';
import { Shield, AlertTriangle, TrendingDown, Eye } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';
import { Spinner } from '../../components/ui/Spinner';

interface FraudMetric {
  date: string;
  total_attempts: number;
  successful: number;
  failed: number;
  blocked_by_radar: number;
  blocked_by_velocity: number;
  high_risk: number;
  block_rate: number;
  high_risk_rate: number;
  avg_risk_score: number;
}

interface BlockedAttempt {
  id: string;
  created_at: string;
  failure_message: string | null;
  risk_score: number | null;
  status: string;
  radar_outcome: string | null;
  profiles: { email: string | null; full_name: string | null } | null;
}

export function AdminFraudMetricsPage() {
  const [metrics, setMetrics] = useState<FraudMetric[]>([]);
  const [blocked, setBlocked] = useState<BlockedAttempt[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);

    const since = new Date();
    since.setDate(since.getDate() - 30);
    const sinceIso = since.toISOString();

    const [metricsRes, blockedRes] = await Promise.all([
      supabase
        .from('fraud_metrics')
        .select('date, total_attempts, successful, failed, blocked_by_radar, blocked_by_velocity, high_risk, block_rate, high_risk_rate, avg_risk_score')
        .gte('date', sinceIso.slice(0, 10))
        .order('date', { ascending: false }),
      supabase
        .from('payment_attempts')
        .select('id, created_at, failure_message, risk_score, status, radar_outcome, profiles:user_id(email, full_name)')
        .or('status.eq.blocked,radar_outcome.eq.block')
        .order('created_at', { ascending: false })
        .limit(100),
    ]);

    setMetrics((metricsRes.data ?? []) as FraudMetric[]);
    setBlocked((blockedRes.data ?? []) as unknown as BlockedAttempt[]);
    setLoading(false);
  }

  const totalAttempts = metrics.reduce((s, r) => s + (r.total_attempts ?? 0), 0);
  const avgBlockRate = metrics.length > 0
    ? metrics.reduce((s, r) => s + (r.block_rate ?? 0), 0) / metrics.length
    : 0;
  const totalHighRisk = metrics.reduce((s, r) => s + (r.high_risk ?? 0), 0);
  const totalBlocked = metrics.reduce((s, r) => s + (r.blocked_by_radar ?? 0) + (r.blocked_by_velocity ?? 0), 0);
  const estimatedSavings = totalBlocked * 50;

  const kpiCards = [
    {
      icon: <Shield size={18} />,
      label: 'Total Tentativas (30d)',
      value: totalAttempts.toLocaleString('pt-BR'),
    },
    {
      icon: <TrendingDown size={18} />,
      label: 'Taxa de Bloqueio',
      value: `${avgBlockRate.toFixed(1)}%`,
    },
    {
      icon: <AlertTriangle size={18} />,
      label: 'Alto Risco',
      value: totalHighRisk.toLocaleString('pt-BR'),
    },
    {
      icon: <Eye size={18} />,
      label: 'Economia Estimada',
      value: `R$ ${estimatedSavings.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title="Métricas de Fraude"
        objective="Monitoramento de tentativas bloqueadas, transações de alto risco e estimativa de economia com prevenção de fraudes. Use para identificar padrões suspeitos e ajustar regras de bloqueio."
      />

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
          <Spinner />
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            {kpiCards.map(card => (
              <div key={card.label} className="card" style={{ padding: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', color: 'var(--text-muted)' }}>
                  {card.icon}
                  <span style={{ fontSize: 'var(--text-caption)' }}>{card.label}</span>
                </div>
                <p style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>{card.value}</p>
              </div>
            ))}
          </div>

          {/* Blocked Attempts Table */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--border-subtle)' }}>
              <h2 style={{ margin: 0, fontSize: 'var(--text-label)', fontWeight: 600, color: 'var(--text-primary)' }}>
                Tentativas Bloqueadas Recentes
              </h2>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Data', 'Usuário', 'Motivo', 'Score de Risco', 'Status'].map(col => (
                    <th key={col} style={{
                      textAlign: 'left',
                      padding: '0.625rem 1rem',
                      fontSize: 'var(--text-caption)',
                      fontWeight: 600,
                      color: 'var(--text-muted)',
                      backgroundColor: 'var(--bg-surface)',
                      borderBottom: '1px solid var(--border-subtle)',
                    }}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {blocked.map(row => (
                  <tr key={row.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    <td style={{ padding: '0.625rem 1rem', fontSize: 'var(--text-caption)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {new Date(row.created_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                    </td>
                    <td style={{ padding: '0.625rem 1rem' }}>
                      <p style={{ margin: 0, fontSize: 'var(--text-label)', color: 'var(--text-primary)', fontWeight: 600 }}>
                        {row.profiles?.full_name ?? '-'}
                      </p>
                      <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>
                        {row.profiles?.email ?? '-'}
                      </p>
                    </td>
                    <td style={{ padding: '0.625rem 1rem', fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', maxWidth: '280px' }}>
                      {row.failure_message ?? row.radar_outcome ?? '-'}
                    </td>
                    <td style={{ padding: '0.625rem 1rem', fontSize: 'var(--text-caption)', color: 'var(--text-primary)', fontWeight: 600 }}>
                      {row.risk_score != null ? row.risk_score.toFixed(2) : '-'}
                    </td>
                    <td style={{ padding: '0.625rem 1rem' }}>
                      <StatusBadgeInline status={row.status} />
                    </td>
                  </tr>
                ))}
                {blocked.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic', padding: '2rem' }}>
                      Nenhuma tentativa bloqueada encontrada
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function StatusBadgeInline({ status }: { status: string }) {
  const map: Record<string, { bg: string; color: string; label: string }> = {
    blocked:  { bg: 'rgba(239,68,68,0.12)',   color: '#f87171', label: 'Bloqueado' },
    failed:   { bg: 'rgba(239,68,68,0.1)',    color: '#fca5a5', label: 'Falhou' },
    pending:  { bg: 'rgba(255,255,255,0.06)', color: 'var(--text-muted)', label: 'Pendente' },
  };
  const style = map[status] ?? { bg: 'rgba(255,255,255,0.06)', color: 'var(--text-muted)', label: status };
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '999px',
      fontSize: '11px',
      fontWeight: 600,
      backgroundColor: style.bg,
      color: style.color,
    }}>
      {style.label}
    </span>
  );
}
